import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import ReplayIcon from "@mui/icons-material/Replay";
import TableChartIcon from "@mui/icons-material/TableChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";

import { Translate } from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";
import { PdfMenu } from "@/components/Archive/Components/History/PdfMenu";
import StepModal from "@/components/Archive/Components/History/StepModal";

import { useMutateArchiveProcedurePdf } from "@/service/ArchiveMutations";
import { useFetchArchivedProcedures } from "@/service/ArchiveQueries";

type Props = {
	options: any;
	initialRowsPerPage?: number;
};

const HistoryTable: FC<Props> = ({ options, initialRowsPerPage }) => {
	const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage || 5);
	const [page, setPage] = useState(0);
	const [open, setOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<any>(null);
	const [loading, setLoading] = useState(false);

	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const { data: archivedProcedures } = useFetchArchivedProcedures(options);
	const createArchivedProcedurePdf = useMutateArchiveProcedurePdf();

	const handleClickOpen = (item: any) => {
		setSelectedItem(item);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedItem(null);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const filteredProcedures = useMemo(() => {
		if (options.SelectedUser === 0) {
			return archivedProcedures?.ArchivedProjects;
		}

		return archivedProcedures?.ArchivedProjects.filter((proc) =>
			proc.Users.some((user) => user.UserId === options.SelectedUser),
		);
	}, [archivedProcedures, options.SelectedUser, options, rowsPerPage]);

	const visibleRows = filteredProcedures?.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleCreatePdf = async (procedureId: number) => {
		setLoading(true);
		try {
			await createArchivedProcedurePdf(procedureId);
		} catch (error) {
			console.error(error);
		}
		setLoading(false);
	};

	return (
		<>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>
								<Translate String="Archive.History.Table.Header.Procedure">
									Procedure
								</Translate>
							</TableCell>
							<TableCell>
								<Translate String="Archive.History.Table.Header.Date">
									Description
								</Translate>
							</TableCell>
							<TableCell>
								<Translate String="Archive.History.Table.Header.Date">
									Date
								</Translate>
							</TableCell>
							<TableCell>
								<Translate String="Archive.History.Table.Header.Actions">
									Actions
								</Translate>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visibleRows?.map((row) => (
							<TableRow key={row.Id}>
								<Tooltip title={row.Name}>
									<TableCell
										sx={{
											minWidth: "50px",
											maxWidth: "inherit",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										<a href={`/archive/procedure/${row.ParentId}`}>
											{row.Name}
										</a>
									</TableCell>
								</Tooltip>
								<Tooltip title={row.Description}>
									<TableCell
										sx={{
											minWidth: "50px",
											maxWidth: "150px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{row.Description}
									</TableCell>
								</Tooltip>
								<TableCell>{dayjs(row.Created).format("DD/MM/YYYY")}</TableCell>
								<TableCell sx={{ display: "table-cell" }}>
									<IconButton
										onClick={() => {
											setSelectedItem(row);
											handleClickOpen(row);
										}}
									>
										<Tooltip title="See analysis">
											<VisibilityIcon sx={{ color: colors.blueAccent[300] }} />
										</Tooltip>
									</IconButton>
									<IconButton>
										<Link to={`/archive/procedure/${row.Id}/data`}>
											<Tooltip title="See all data">
												<TableChartIcon
													sx={{ color: colors.greenAccent[500] }}
												/>
											</Tooltip>
										</Link>
									</IconButton>
									{row.FileCreated ? (
										<PdfMenu row={row} pdfColor={colors.redAccent[500]} />
									) : (
										<IconButton
											onClick={async () => await handleCreatePdf(row.Id)}
											loading={loading}
										>
											<ReplayIcon />
										</IconButton>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={filteredProcedures?.length || 0}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</TableContainer>
			{selectedItem && (
				<StepModal
					open={open}
					handleClose={handleClose}
					selectedItem={selectedItem}
				/>
			)}
		</>
	);
};

export default HistoryTable;
